import { Site } from './site';
import { Node } from '../base/node';
import { NodeBatch } from './node-batch';
import { NodeSolo } from './node-solo';
import { Content } from './content';
import { NodeInfo } from './node-info';
import { Secure } from '../base/secure';
import { AccessLevel } from '../security/access-level';
import { Genus } from './genus';
import { Syndicate } from './syndicate';
import { Branch } from '../base/branch';

export class NodeModel {
    info: NodeInfo;

    children?: NodeModel[];
    descents?: NodeModel[];

    batch?: NodeBatch;
    solo: NodeSolo = new NodeSolo();

    moved: boolean = false;
    obscure: boolean = false;
    loaded: boolean = false;
    readonly: boolean = false;

    custome: any = {};

    constructor(
        public readonly node: Node,
        private readonly loadBatch: boolean,
        public parent?: NodeModel,
        public site?: Site,
        private silent: boolean = false
    ) {
        this.info = new NodeInfo(node);

        if (loadBatch) {
            this.batch = new NodeBatch();
        }
        this.init();
    }

    toggleSolo(): void {
        this.solo.expanded = !this.solo.expanded;
    }

    toggleBatch(): void {
        if (this.batch) {
            this.batch.expanded = !this.batch.expanded;
        }
    }

    edit(): void {
        this.solo.active = true;
        this.solo.editing = true;
    }

    save(): void {
        this.solo.editing = false;
    }

    cancel(): void {
        this.solo.editing = false;
        this.node.name = this.node.original;
    }

    actify(active: boolean): void {
        this.solo.active = active;
        if (!active) {
            this.solo.editing = false;
        }
        let nodeModel: NodeModel = this;
        if (active) {
            while (nodeModel.parent) {
                nodeModel.parent.solo.expanded = true;
                nodeModel = nodeModel.parent;
            }
        }
    }

    reset(update: boolean): void {
        this.update();

        if (update) {
            if (this.solo.editing) {
                this.solo.editing = false;
            }

            this.setObscure();
        }
    }

    private update(): void {
        if (this.node._) {
            delete this.node._;
            this.loaded = true;
        }

        if (this.node instanceof Secure) {
            this.solo.expandable = true;
            this.solo.editable = this.node.accessLevel > AccessLevel.List;
        } else {
            this.solo.editable = true;
        }
    }

    load(source?: any, action?: (model: NodeModel) => void): void {
        if (this.loaded) return;

        this.loaded = true;

        if (source) {
            this.node.load(source);
            if (this.node instanceof Content) {
                this.readonly = (<Secure>this.node.parent).accessLevel <= AccessLevel.List;
            } else if (this.node instanceof Syndicate) {
                this.node.parent = this.info.branch;
            }
        }

        this.update();

        this.info.branch?.nodes.forEach(node => {
            const model = new NodeModel(node, this.loadBatch, this, node instanceof Site ? node : this.site);
            node.parent = this.info.branch;

            this.children?.push(model);

            if (action) {
                action(model);
            }
        });

        this.info.branch?.branches.forEach(branch => {
            const model = new NodeModel(branch, this.loadBatch, this, branch instanceof Site ? branch : this.site);
            branch.parent = this.info.branch;

            this.descents?.push(model);

            if (action) {
                action(model);
            }
        });
    }

    add(node: Node, silent: boolean = false, insert?: (parent: NodeModel, child: NodeModel) => void): NodeModel {
        const model = new NodeModel(node, this.loadBatch, this, node instanceof Site ? node : this.site, silent);
        const parent = this.info.branch;

        if (insert) {
            insert(this, model);
        } else if (node instanceof Branch) {
            this.descents?.push(model);
            parent?.branches.push(node)
        } else {
            this.children?.push(model);
            parent?.nodes.push(node);
        }

        return model;
    }

    delete() {
        if (this.parent) {
            if (this.node instanceof Branch) {
                if (this.parent.descents?.length) {
                    const index = this.parent.descents.indexOf(this);
                    this.parent.descents.splice(index, 1);
                }
            } else {
                if (this.parent.children?.length) {
                    const index = this.parent.children.indexOf(this);
                    this.parent.children.splice(index, 1);
                }
            }

            if (!this.parent.descents?.length && !this.parent.children?.length) {
                this.parent.loaded = false;
            }
        }
    }

    replace(parent: NodeModel) {
        if (parent) {
            this.delete();
            this.parent = parent;
            this.site = parent.site;
            if (this.node instanceof Branch) {
                parent.descents?.push(this);
            } else {
                parent.children?.push(this);
            }
            parent.solo.expanded = true;
            this.setObscure();
        }
    }

    private init(): void {
        if (this.node) {
            if (this.info.branch) {
                this.children = [];
                this.descents = [];

                this.solo.canDrag = !this.info.site && !!this.node.parentId;
                this.solo.canDrop = !(this.info.genus && !this.node.parentId);
            } else {
                this.solo.canDrag = true;
            }

            this.setObscure();

            if (!this.silent && this.node.isNew && !this.solo.moved) {
                this.solo.editable = true;
                this.edit();
            }
        }
    }

    private setObscure(): void {
        this.obscure = (this.parent?.obscure ?? false) || (this.node instanceof Genus || this.node instanceof Content ? (this.node.publish?.obscure ?? false) : false);
        this.children?.forEach(child => child.setObscure());
        this.descents?.forEach(descent => descent.setObscure());
    }
}
